import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import PricingPlans from '@/components/PricingPlans';
import PublicHeader from '@/components/layout/PublicHeader';
import { useLocale } from '@/contexts/LocaleContext';

const Pricing = () => {
  const navigate = useNavigate();
  const { isUSA } = useLocale();

  return (
    <div className="min-h-screen bg-slate-50" data-testid="pricing-page">
      {/* Unified Public Header */}
      <PublicHeader showBack={true} backPath="/" />

      {/* Header */}
      <section className="py-16 bg-gradient-to-b from-white to-slate-50">
        <div className="container mx-auto px-4 max-w-7xl text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4" style={{fontFamily: 'Manrope, sans-serif'}}>
            {isUSA ? 'Simple Pricing' : 'Precios Simples'}
          </h1>
          <p className="text-xl text-slate-600 max-w-2xl mx-auto">
            {isUSA 
              ? 'Start free, upgrade when you need more clients'
              : 'Empieza gratis, actualiza cuando necesites más clientes'}
          </p>
        </div>
      </section>

      {/* Pricing Plan */}
      <section className="py-12 px-4">
        <div className="container mx-auto max-w-7xl">
          <PricingPlans showHeader={false} />
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 max-w-3xl">
          <h2 className="text-2xl font-bold text-slate-900 mb-8 text-center" style={{fontFamily: 'Manrope, sans-serif'}}>
            {isUSA ? 'Frequently asked questions' : 'Preguntas frecuentes'}
          </h2>
          <div className="space-y-6">
            <div className="p-6 bg-slate-50 rounded-xl">
              <h3 className="font-semibold text-slate-900 mb-2">
                {isUSA ? 'How does the Free plan work?' : '¿Cómo funciona el plan Gratis?'}
              </h3>
              <p className="text-slate-600">
                {isUSA 
                  ? 'Create your account, add up to 5 products and activate your basic online store (with the "Made with TexHub" badge). No credit card required. Upgrade to PRO anytime for unlimited products, no badge and full AI tools.'
                  : 'Crea tu cuenta, agrega hasta 5 productos y activa tu tienda en línea básica (con el sello "Hecho con TexHub"). Sin tarjeta de crédito. Mejora a PRO cuando quieras para productos ilimitados, sin sello y herramientas IA completas.'}
              </p>
            </div>
            <div className="p-6 bg-slate-50 rounded-xl">
              <h3 className="font-semibold text-slate-900 mb-2">
                {isUSA ? 'Can I cancel anytime?' : '¿Puedo cancelar en cualquier momento?'}
              </h3>
              <p className="text-slate-600">
                {isUSA 
                  ? 'Yes, cancel anytime from your dashboard. No questions asked. You keep access until the end of your billing period.'
                  : 'Sí, cancela cuando quieras desde tu panel. Sin preguntas. Mantienes acceso hasta el final de tu período de facturación.'}
              </p>
            </div>
            <div className="p-6 bg-slate-50 rounded-xl">
              <h3 className="font-semibold text-slate-900 mb-2">
                {isUSA ? 'What payment methods do you accept?' : '¿Qué métodos de pago aceptan?'}
              </h3>
              <p className="text-slate-600">
                {isUSA 
                  ? 'We accept all major credit and debit cards (Visa, Mastercard, American Express).'
                  : 'Aceptamos tarjetas de crédito/débito (Visa, Mastercard, American Express).'}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-emerald-600">
        <div className="container mx-auto px-4 max-w-3xl text-center">
          <h2 className="text-3xl font-bold text-white mb-4" style={{fontFamily: 'Manrope, sans-serif'}}>
            {isUSA ? 'Ready to grow your business?' : '¿Listo para hacer crecer tu negocio?'}
          </h2>
          <p className="text-emerald-100 mb-8">
            {isUSA 
              ? 'Suppliers from Mexico City, Estado de México and Monterrey already sell on TexHub'
              : 'Proveedores de CDMX, Estado de México y Monterrey ya venden en TexHub'}
          </p>
          <Button 
            size="lg"
            className="bg-white text-emerald-700 hover:bg-emerald-50 font-bold px-8"
            onClick={() => navigate('/register')}
          >
            {isUSA ? 'Start free' : 'Empieza gratis'}
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 bg-slate-950 text-slate-500 text-sm">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p>© 2026 TexHub. {isUSA ? 'All rights reserved.' : 'Todos los derechos reservados.'}</p>
            <div className="flex gap-6">
              <a href="/terms" className="hover:text-white">{isUSA ? 'Terms' : 'Términos'}</a>
              <a href="/privacy" className="hover:text-white">{isUSA ? 'Privacy' : 'Privacidad'}</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Pricing;
